import React from "react";
import { AccountModel } from "shared/model/AccountModel";
import styles from "./UserAvatar.module.scss";

interface UserAvatarProps {
	/** Modelo do usuário (preferido). Se ausente, use `name` + `imageUrl` manualmente. */
	user?: AccountModel | null;
	/** Nome usado para gerar as iniciais quando não há `user`. */
	name?: string;
	/** URL da imagem; sobrescreve a do user quando passada. */
	imageUrl?: string | null;
	/** Tamanho em pixels (largura = altura). Default 40. */
	size?: number;
	/** Classe adicional para o wrapper. */
	className?: string;
	/** Mostra um indicador (ring) ao passar o mouse — útil para botões clicáveis. */
	interactive?: boolean;
	/** ARIA label customizado. */
	ariaLabel?: string;
}

/**
 * Avatar do usuário com fallback visual da identidade do e-Keep.
 * - Se houver `imageUrl` (ou `user.profilePictureUrl`), renderiza a foto.
 * - Caso contrário, exibe um gradient verde-da-marca com as iniciais em branco.
 *
 * Substitui o padrão antigo (URL hardcoded do Google) por uma identidade própria.
 */
export const UserAvatar: React.FC<UserAvatarProps> = ({
	user,
	name,
	imageUrl,
	size = 40,
	className,
	interactive,
	ariaLabel,
}) => {
	const resolvedImage = imageUrl !== undefined ? imageUrl : user?.profilePictureUrl ?? null;
	const resolvedName = name ?? user?.displayName ?? user?.username ?? "";
	const initials = getInitials(resolvedName);
	const label = ariaLabel ?? (resolvedName ? `Avatar de ${resolvedName}` : "Avatar do usuário");

	const baseStyle: React.CSSProperties = {
		width: size,
		height: size,
		fontSize: Math.max(11, Math.round(size * 0.4)),
	};

	if (resolvedImage) {
		return (
			<div
				className={`${styles.wrapper} ${styles.withImage} ${interactive ? styles.interactive : ""} ${className ?? ""}`}
				style={{ ...baseStyle, backgroundImage: `url(${resolvedImage})` }}
				role="img"
				aria-label={label}
			/>
		);
	}

	return (
		<div
			className={`${styles.wrapper} ${styles.fallback} ${interactive ? styles.interactive : ""} ${className ?? ""}`}
			style={baseStyle}
			role="img"
			aria-label={label}
		>
			<span className={styles.initials}>{initials || "·"}</span>
		</div>
	);
};

function getInitials(name: string): string {
	if (!name) return "";
	const parts = name.trim().split(/\s+/).filter(Boolean);
	if (parts.length === 0) return "";
	if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
	return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
}
